import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import './UserAccounts.css';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import SearchSelectBoxDrown from '../../../components/common/SelectBox/searchSelect';
import PhoneInputSelect from '../../../components/common/SelectBox/PhoneInputSelect';
import EditUploadBoxCountry from '../../../components/common/InputBox/EditUpload';
import ErrorNotification from '../../../components/common/AlertBox/ErrorNotification';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';
import authenticatedAxios from '../../../authenticatedAxios';
import { baseurl } from '../../../api';
import { fetchActiveCountryCode, fetchActiveLanguages, fetchTimezone } from '../../../services/Localization/CountryService';
import { fetchActiveOrgUserRoles } from '../../../services/Subscriptions/OrgUserService';
import { fetchActiveOrgs } from '../../../services/Subscriptions/CustomerService';

const UserAccountEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [countries, setCountries] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [timezones, setTimezones] = useState([]);
    const [orgData, setOrgData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        organization: '',
        full_name: '',
        email: '',
        phone: '',
        country_code: '',
        user_type: '',
        role: '',
        preferred_language: '',
        timezone: '',
        profile_pic: null,
        existingProfilePic: '',
    });

    useEffect(() => {
        async function loadInitialData() {
            try {
                const [
                    countriesData,
                    userRolesData,
                    timezonesData,
                    languagesData,
                    organisationData
                ] = await Promise.all([
                    fetchActiveCountryCode(),
                    fetchActiveOrgUserRoles(),
                    fetchTimezone(),
                    fetchActiveLanguages(),
                    fetchActiveOrgs()
                ]);

                setCountries(countriesData || []);
                setUserRoles(userRolesData || []);
                setTimezones(timezonesData || []);
                setLanguages(languagesData || []);
                setOrgData(organisationData || []);
            } catch (error) {
                console.error('Error loading dropdown data:', error);
            }
        }
        loadInitialData();
    }, []);

    useEffect(() => {
        const fetchUserAccount = async () => {
            try {
                const response = await authenticatedAxios.get(`${baseurl}accounts/users/organizational/?id=${id}`);
                if (response.data && response.data.response_code === 1) {
                    const userData = response.data.data;
                    setForm({
                        organization: userData.organization?.id || userData.organization || '',
                        full_name: userData.full_name || '',
                        email: userData.email || '',
                        phone: userData.phone || '',
                        country_code: userData.country_code?.id || userData.country_code || '',
                        user_type: userData.user_type || '',
                        role: userData.role?.id || userData.role || '',
                        preferred_language: userData.preferred_language?.id || userData.preferred_language || '',
                        timezone: userData.timezone?.id || userData.timezone || '',
                        existingProfilePic: userData.profile_pic || '',
                        profile_pic: null,
                    });
                }
            } catch (error) {
                console.error('Error fetching user account details:', error);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchUserAccount();
        }
    }, [id]);

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!form.organization) {
            newErrors.organization = ["Company Name is required"];
        }
        if (!form.full_name?.trim()) {
            newErrors.full_name = ["User Name is required"];
        }
        if (!form.email?.trim()) {
            newErrors.email = ["Email is required"];
        }
        if (!form.phone?.trim()) {
            newErrors.phone = ["Mobile Number is required"];
        }
        if (!form.country_code) {
            newErrors.country_code = ["Country Code is required"];
        }
        if (!form.user_type) {
            newErrors.user_type = ["User Type is required"];
        }
        
        if (!form.preferred_language) {
            newErrors.preferred_language = ["Preferred Language is required"];
        }
        if (!form.timezone) {
            newErrors.timezone = ["User Time Zone is required"];
        }

        return newErrors;
    };

    const handleSubmit = async () => {
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            const formData = new FormData();
            if (form.profile_pic instanceof File) {
                formData.append('profile_pic', form.profile_pic);
            }
            formData.append('organization', form.organization);
            formData.append('full_name', form.full_name);
            formData.append('email', form.email);
            formData.append('phone', form.phone);
            formData.append('country_code', form.country_code);
            formData.append('user_type', form.user_type);
            formData.append('role', form.role);
            formData.append('preferred_language', form.preferred_language);
            formData.append('timezone', form.timezone);

            const response = await authenticatedAxios.put(
                `${baseurl}accounts/users/organizational/?id=${id}`,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            );

            if (response.data.response_code === 1) {
                SuccessAlert("User updated successfully");
                navigate(`/user-accounts/detail/${id}`);
            } else {
                const msg = response.data.message;
                if (msg && typeof msg === 'object') {
                    setErrors(msg);
                } else {
                    setErrors({ general: msg || "Failed to update user account" });
                }
            }
        } catch (error) {
            console.error("Error updating user:", error);
            if (error.response?.data?.message) {
                const msg = error.response.data.message;
                if (typeof msg === 'object') {
                    setErrors(msg);
                } else {
                    setErrors({ general: msg });
                }
            } else {
                setErrors({ general: "Error updating user account. Please try again." });
            }
        }
    };

    if (loading) {
        return <div className="whole-container">Loading...</div>;
    }

    return (
        <div className='whole-container'>
            <div className='top-heading'>
                <PageHeader pageName="Subscription / User Accounts /" nestedpageName="Edit" linkTo="/user-accounts" />
                <div className='export-container'>
                    <ExportLabel />
                </div>
            </div>
            <div className="page-container">
                <ErrorNotification errors={errors} marginBottom="16px" />
                
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">User Details</span>
                        <button className="popup-add-button common-text white fts-15 ftw-5" onClick={handleSubmit}>
                            Save Changes
                        </button>
                    </div>

                    <div className="card-body">
                        <div className="row-2">
                            <SearchSelectBoxDrown
                                label="Company Name"
                                options={orgData}
                                isRequired
                                defaultValue={form.organization}
                                onChange={(e) => handleChange('organization', e.target.value)}
                            />
                            <InputBoxSmall
                                label="User Name"
                                isRequired
                                defaultValue={form.full_name}
                                onChange={(e) => handleChange('full_name', e.target.value)}
                            />
                        </div>

                        <div className="row-2">
                            <InputBoxSmall
                                label="Email"
                                isRequired
                                defaultValue={form.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                            />
                            <PhoneInputSelect
                                label="Mobile Number"
                                options={countries}
                                isRequired
                                defaultOption={form.country_code}
                                defaultValue={form.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                selectchange={(e) => handleChange('country_code', e.target.value)}
                            />
                        </div>

                        <div className="row-2">
                            <SearchSelectBoxDrown
                                label="User Type"
                                options={[
                                    { label: 'Vansaler', value: 'Vansaler' },
                                    { label: 'Employee', value: 'Employee' },
                                    { label: 'Both', value: 'Both' }
                                ]}
                                isRequired
                                defaultValue={form.user_type}
                                onChange={(e) => handleChange('user_type', e.target.value)}
                            />
                            <SearchSelectBoxDrown
                                label="User Role"
                                options={userRoles}
                                defaultValue={form.role}
                                onChange={(e) => handleChange('role', e.target.value)}
                            />
                        </div>

                        <div className="row-2">
                            <SearchSelectBoxDrown
                                label="Preferred Language"
                                options={languages}
                                isRequired
                                defaultValue={form.preferred_language}
                                onChange={(e) => handleChange('preferred_language', e.target.value)}
                            />
                            <SearchSelectBoxDrown
                                label="User Time Zone"
                                options={timezones}
                                isRequired
                                defaultValue={form.timezone}
                                onChange={(e) => handleChange('timezone', e.target.value)}
                            />
                        </div>

                        <EditUploadBoxCountry
                            label="Profile Picture"
                            existingImage={
                                form.existingProfilePic
                                    ? `${baseurl.replace(/\/$/, '')}${form.existingProfilePic.startsWith('/') ? '' : '/'}${form.existingProfilePic}`
                                    : "/assets/avatar.jpg"
                            }
                            onFileChange={(file) => handleChange('profile_pic', file)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserAccountEdit;
